import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

const SidebarFlyoutContext = createContext(null);

/**
 * Un identificador de panel desconocido NO puede ser una funcion vacia.
 *
 * Era eso, y produjo un clic que no hacia nada y no decia nada: sin error, sin
 * cambio de URL, sin peticion. Tres dias para encontrarlo.
 *
 * En DESARROLLO revienta, que es donde se escribe el mapa y donde tiene que
 * doler. En PRODUCCION no tumba la pantalla --un menu roto es malo, una pantalla
 * en blanco es peor-- pero deja el motivo en la consola con el nombre delante,
 * asi que deja de ser silencio.
 */
function denunciarFlyoutSinId(desde) {
  const mensaje =
    `[SidebarFlyout] ${desde}() recibio un flyoutId vacio. Casi siempre significa `
    + 'que `section.id` no esta en HR_FLYOUT_IDS / ADMIN_FLYOUT_IDS: el panel no '
    + 'se abrira y el clic no hara NADA, en silencio.';
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    throw new Error(mensaje);
  }
  // eslint-disable-next-line no-console
  console.error(mensaje);
}

export const ADMIN_FLYOUT_IDS = {
  finance: 'administration-finance-flyout',
  procurement: 'administration-procurement-flyout',
  inventory: 'administration-inventory-flyout',
  catering: 'administration-catering-flyout',
};

/**
 * Las secciones de HR. **Tienen que coincidir con los `section.id` que produce
 * `buildHrFlyoutSections`** (`hr/hrNavConfig.js`).
 *
 * Cuando dejaron de coincidir, `HR_FLYOUT_IDS[section.id]` dio `undefined`,
 * `useSidebarFlyout` devolvio un `openFlyout` vacio y **el clic no hizo nada, sin
 * un solo error en consola**. La ruta estaba en `allowedRoutes`, la pantalla
 * funcionaba escribiendo la URL, y el resto del sidebar navegaba: el corte
 * estaba aqui, entre el rotulo y el panel.
 *
 * Lo guarda `hrSidebarNavigation.test.js`, que RENDERIZA el sidebar y pulsa cada
 * entrada.
 */
export const HR_FLYOUT_IDS = {
  recruitment: 'hr-recruitment-flyout',
  personnel: 'hr-personnel-flyout',
  timesheet: 'hr-timesheet-flyout',
  payroll: 'hr-payroll-flyout',
  reports: 'hr-reports-flyout',
  config: 'hr-config-flyout',
};

export function SidebarFlyoutProvider({ children }) {
  const [activeFlyoutId, setActiveFlyoutId] = useState(null);
  const location = useLocation();
  const routeKeyRef = useRef('');

  // Close flyouts only when the route actually changes (not on every render).
  useEffect(() => {
    const routeKey = `${location.pathname}${location.search}`;
    if (routeKeyRef.current === routeKey) return;
    routeKeyRef.current = routeKey;
    setActiveFlyoutId(null);
  }, [location.pathname, location.search]);

  const closeFlyout = useCallback(() => {
    setActiveFlyoutId(null);
  }, []);

  const openFlyout = useCallback((flyoutId) => {
    if (!flyoutId) {
      denunciarFlyoutSinId('openFlyout');
      return;
    }
    setActiveFlyoutId(flyoutId);
  }, []);

  const toggleFlyout = useCallback((flyoutId) => {
    if (!flyoutId) {
      denunciarFlyoutSinId('toggleFlyout');
      return;
    }
    setActiveFlyoutId((current) => (current === flyoutId ? null : flyoutId));
  }, []);

  const value = useMemo(
    () => ({
      activeFlyoutId,
      openFlyout,
      toggleFlyout,
      closeFlyout,
    }),
    [activeFlyoutId, openFlyout, toggleFlyout, closeFlyout],
  );

  return (
    <SidebarFlyoutContext.Provider value={value}>
      {children}
    </SidebarFlyoutContext.Provider>
  );
}

export function useSidebarFlyout(flyoutId) {
  const ctx = useContext(SidebarFlyoutContext);
  if (!ctx || !flyoutId) {
    // Sin contexto es legitimo -- una fila fuera del proveedor. Sin `flyoutId`
    // NO lo es: significa que alguien pidio un panel que no esta en el mapa, y
    // devolver funciones vacias convierte eso en un clic que no hace nada.
    if (ctx && !flyoutId) denunciarFlyoutSinId('useSidebarFlyout');
    return {
      open: false,
      openFlyout: () => {},
      toggleFlyout: () => {},
      closeFlyout: () => {},
    };
  }

  return {
    open: ctx.activeFlyoutId === flyoutId,
    openFlyout: () => ctx.openFlyout(flyoutId),
    toggleFlyout: () => ctx.toggleFlyout(flyoutId),
    closeFlyout: ctx.closeFlyout,
  };
}

export function useSidebarFlyoutControl() {
  const ctx = useContext(SidebarFlyoutContext);
  return {
    closeAllFlyouts: ctx?.closeFlyout ?? (() => {}),
    activeFlyoutId: ctx?.activeFlyoutId ?? null,
  };
}
